import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Heading,
  Button,
  Card,
  CardHeader,
  CardBody,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  useToast,
  VStack,
  HStack,
  Divider,
  SimpleGrid,
  Text,
  Alert,
  AlertIcon,
  Switch,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Flex,
} from '@chakra-ui/react';
import { CheckIcon } from '@chakra-ui/icons';
import { api } from '../../services/api';
import AdminLayout from '../../layouts/AdminLayout';

interface InvoiceSettings {
  supplierICO: string;
  supplierName: string;
  supplierDIC: string;
  supplierAddress: string;
  supplierCity: string;
  supplierZIP: string;
  supplierCountry: string;
  supplierEmail: string;
  supplierPhone: string;
  supplierWebsite: string;
  bankName: string;
  bankAccount: string;
  iban: string;
  swift: string;
  defaultDueDays: number;
  defaultVATRate: number;
  invoicePrefix: string;
  nextInvoiceNumber: number;
  emailSubject: string;
  emailBody: string;
  paymentInstructions: string;
  legalNote: string;
  includeQRCode: boolean;
  includeSignature: boolean;
  currency: string;
  language: string;
  isVATPayer: boolean;
}

const InvoiceSettingsPage: React.FC = () => {
  const [settings, setSettings] = useState<InvoiceSettings>({
    supplierICO: '',
    supplierName: '',
    supplierDIC: '',
    supplierAddress: '',
    supplierCity: '',
    supplierZIP: '',
    supplierCountry: 'Česká republika',
    supplierEmail: '',
    supplierPhone: '',
    supplierWebsite: '',
    bankName: '',
    bankAccount: '',
    iban: '',
    swift: '',
    defaultDueDays: 14,
    defaultVATRate: 21,
    invoicePrefix: 'FV',
    nextInvoiceNumber: 1,
    emailSubject: 'Faktura č. {invoiceNumber}',
    emailBody: 'Dobrý den,\n\nv příloze zasíláme fakturu č. {invoiceNumber} ve formátu PDF.\n\nFaktura je splatná do {dueDate}.\n\nDěkujeme\n{supplierName}',
    paymentInstructions: 'Platba provedená bankovním převodem na účet č. {bankAccount}',
    legalNote: 'Podle § 13 Zákona o dani z přidané hodnoty č. 235/2004 Sb. je tento doklad daňovým dokladem.',
    includeQRCode: true,
    includeSignature: false,
    currency: 'CZK',
    language: 'cs',
    isVATPayer: true,
  });
  
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSearchingICO, setIsSearchingICO] = useState(false);
  const toast = useToast();

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setIsLoading(true);
      const response = await api.get('/admin/invoices/settings');
      if (response.data.settings) {
        setSettings(response.data.settings);
      }
    } catch (error) {
      toast({
        title: 'Chyba při načítání nastavení',
        description: 'Nepodařilo se načíst nastavení fakturace',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      setIsSaving(true);
      await api.put('/admin/invoices/settings', settings);

      toast({
        title: 'Nastavení uloženo',
        description: 'Nastavení fakturace bylo úspěšně uloženo',
        status: 'success',
        duration: 3000,
      });
    } catch (error: any) {
      toast({
        title: 'Chyba při ukládání',
        description: error?.response?.data?.message || 'Nepodařilo se uložit nastavení',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const updateSetting = (field: keyof InvoiceSettings, value: any) => {
    setSettings({ ...settings, [field]: value });
  };

  const searchICO = async () => {
    if (!settings.supplierICO || settings.supplierICO.length < 8) {
      toast({
        title: 'Chyba',
        description: 'Zadejte platné IČO (alespoň 8 číslic)',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    try {
      setIsSearchingICO(true);
      const response = await api.get(`/admin/invoices/ares-search/${settings.supplierICO}`);
      
      if (response.data.found) {
        const companyData = response.data;
        setSettings({
          ...settings,
          supplierName: companyData.name || settings.supplierName,
          supplierAddress: companyData.address || settings.supplierAddress,
          supplierCity: companyData.city || settings.supplierCity,
          supplierZIP: companyData.zip || settings.supplierZIP,
          supplierDIC: companyData.vatId || settings.supplierDIC,
        });
        
        toast({
          title: 'Údaje nalezeny',
          description: `Společnost ${companyData.name} byla nalezena v ARES`,
          status: 'success',
          duration: 3000,
        });
      } else {
        toast({
          title: 'Společnost nenalezena',
          description: 'Společnost s tímto IČO nebyla nalezena v ARES',
          status: 'warning',
          duration: 3000,
        });
      }
    } catch (error: any) {
      toast({
        title: 'Chyba při vyhledávání',
        description: error?.response?.data?.message || 'Nepodařilo se vyhledat údaje v ARES',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setIsSearchingICO(false);
    }
  };

  return (
    <AdminLayout>
      <Container maxW="container.xl" py={8}>
        <VStack spacing={6} align="stretch">
        {/* Header */}
        <Flex justify="space-between" align="center">
          <Heading size="lg">Nastavení fakturace</Heading>
          <Button
            leftIcon={<CheckIcon />}
            colorScheme="blue"
            onClick={handleSaveSettings}
            isLoading={isSaving}
          >
            Uložit nastavení
          </Button>
        </Flex>

        {/* ICO Section */}
        <Card>
          <CardHeader>
            <Heading size="md">Identifikační číslo organizace (IČO)</Heading>
          </CardHeader>
          <CardBody>
            <VStack spacing={4} align="stretch">
              <FormControl>
                <FormLabel>IČO</FormLabel>
                <HStack spacing={2}>
                  <Input
                    value={settings.supplierICO}
                    onChange={(e) => updateSetting('supplierICO', e.target.value)}
                    placeholder="12345678"
                    maxLength={8}
                  />
                  <Button
                    onClick={searchICO}
                    isLoading={isSearchingICO}
                    colorScheme="blue"
                    variant="outline"
                  >
                    Vyhledat v ARES
                  </Button>
                </HStack>
                <Text fontSize="sm" color="gray.600" mt={1}>
                  Zadejte IČO a klikněte na "Vyhledat v ARES" pro automatické doplnění údajů o společnosti.
                </Text>
              </FormControl>

              <FormControl display="flex" alignItems="center">
                <FormLabel mb="0">Nejsme plátce DPH</FormLabel>
                <Switch
                  isChecked={!settings.isVATPayer}
                  onChange={(e) => updateSetting('isVATPayer', !e.target.checked)}
                />
                <Text fontSize="sm" color="gray.600" ml={2}>
                  Pokud zaškrtnete, DPH nebude připočítáváno k fakturám
                </Text>
              </FormControl>
            </VStack>
          </CardBody>
        </Card>

        {/* Company Information */}
        <Card>
          <CardHeader>
            <Heading size="md">Údaje o dodavateli</Heading>
          </CardHeader>
          <CardBody>
            <VStack spacing={4} align="stretch">
              <FormControl>
                <FormLabel>Název firmy</FormLabel>
                <Input
                  value={settings.supplierName}
                  onChange={(e) => updateSetting('supplierName', e.target.value)}
                  placeholder="Fotbal Club s.r.o."
                />
              </FormControl>
              
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <FormControl>
                  <FormLabel>DIČ</FormLabel>
                  <Input
                    value={settings.supplierDIC}
                    onChange={(e) => updateSetting('supplierDIC', e.target.value)}
                    placeholder="CZ1234567890"
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Stát</FormLabel>
                  <Input
                    value={settings.supplierCountry}
                    onChange={(e) => updateSetting('supplierCountry', e.target.value)}
                    placeholder="Česká republika"
                  />
                </FormControl>
              </SimpleGrid>

              <FormControl>
                <FormLabel>Adresa</FormLabel>
                <Input
                  value={settings.supplierAddress}
                  onChange={(e) => updateSetting('supplierAddress', e.target.value)}
                  placeholder="Hlavní 123"
                />
              </FormControl>

              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <FormControl>
                  <FormLabel>Město</FormLabel>
                  <Input
                    value={settings.supplierCity}
                    onChange={(e) => updateSetting('supplierCity', e.target.value)}
                    placeholder="Praha"
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>PSČ</FormLabel>
                  <Input
                    value={settings.supplierZIP}
                    onChange={(e) => updateSetting('supplierZIP', e.target.value)}
                    placeholder="123 45"
                  />
                </FormControl>
              </SimpleGrid>

              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                <FormControl>
                  <FormLabel>Email</FormLabel>
                  <Input
                    type="email"
                    value={settings.supplierEmail}
                    onChange={(e) => updateSetting('supplierEmail', e.target.value)}
                    placeholder="info@fotbalclub.cz"
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Telefon</FormLabel>
                  <Input
                    value={settings.supplierPhone}
                    onChange={(e) => updateSetting('supplierPhone', e.target.value)}
                    placeholder="+420 123 456 789"
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Website</FormLabel>
                  <Input
                    value={settings.supplierWebsite}
                    onChange={(e) => updateSetting('supplierWebsite', e.target.value)}
                    placeholder="www.fotbalclub.cz"
                  />
                </FormControl>
              </SimpleGrid>
            </VStack>
          </CardBody>
        </Card>

        {/* Bank Information */}
        <Card>
          <CardHeader>
            <Heading size="md">Bankovní údaje</Heading>
          </CardHeader>
          <CardBody>
            <VStack spacing={4} align="stretch">
              <FormControl>
                <FormLabel>Název banky</FormLabel>
                <Input
                  value={settings.bankName}
                  onChange={(e) => updateSetting('bankName', e.target.value)}
                  placeholder="Česká národní banka"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Číslo účtu</FormLabel>
                <Input
                  value={settings.bankAccount}
                  onChange={(e) => updateSetting('bankAccount', e.target.value)}
                  placeholder="123456789/0300"
                />
              </FormControl>

              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <FormControl>
                  <FormLabel>IBAN</FormLabel>
                  <Input
                    value={settings.iban}
                    onChange={(e) => updateSetting('iban', e.target.value)}
                    placeholder="CZ12345678901234567890123456"
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>SWIFT/BIC</FormLabel>
                  <Input
                    value={settings.swift}
                    onChange={(e) => updateSetting('swift', e.target.value)}
                    placeholder="GIBACZPX"
                  />
                </FormControl>
              </SimpleGrid>
            </VStack>
          </CardBody>
        </Card>

        {/* Invoice Settings */}
        <Card>
          <CardHeader>
            <Heading size="md">Nastavení faktur</Heading>
          </CardHeader>
          <CardBody>
            <VStack spacing={4} align="stretch">
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                <FormControl>
                  <FormLabel>Předpona čísla faktury</FormLabel>
                  <Input
                    value={settings.invoicePrefix}
                    onChange={(e) => updateSetting('invoicePrefix', e.target.value)}
                    placeholder="FV"
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Další číslo faktury</FormLabel>
                  <NumberInput
                    value={settings.nextInvoiceNumber}
                    onChange={(value) => updateSetting('nextInvoiceNumber', parseInt(value) || 1)}
                    min={1}
                  >
                    <NumberInputField />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                </FormControl>
                <FormControl>
                  <FormLabel>Výchozí měna</FormLabel>
                  <Select
                    value={settings.currency}
                    onChange={(e) => updateSetting('currency', e.target.value)}
                  >
                    <option value="CZK">CZK</option>
                    <option value="EUR">EUR</option>
                    <option value="USD">USD</option>
                  </Select>
                </FormControl>
              </SimpleGrid>

              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <FormControl>
                  <FormLabel>Výchozí splatnost (dny)</FormLabel>
                  <NumberInput
                    value={settings.defaultDueDays}
                    onChange={(value) => updateSetting('defaultDueDays', parseInt(value) || 14)}
                    min={0}
                    max={365}
                  >
                    <NumberInputField />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                </FormControl>
                <FormControl>
                  <FormLabel>Výchozí sazba DPH (%)</FormLabel>
                  <Select
                    value={settings.defaultVATRate}
                    onChange={(e) => updateSetting('defaultVATRate', parseFloat(e.target.value))}
                  >
                    <option value="0">0%</option>
                    <option value="10">10%</option>
                    <option value="15">15%</option>
                    <option value="21">21%</option>
                  </Select>
                </FormControl>
              </SimpleGrid>

              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <FormControl>
                  <FormLabel>Jazyk faktury</FormLabel>
                  <Select
                    value={settings.language}
                    onChange={(e) => updateSetting('language', e.target.value)}
                  >
                    <option value="cs">Čeština</option>
                    <option value="en">English</option>
                    <option value="de">Deutsch</option>
                  </Select>
                </FormControl>
                <FormControl display="flex" alignItems="center">
                  <FormLabel mb="0">Přidat QR kód pro platbu</FormLabel>
                  <Switch
                    isChecked={settings.includeQRCode}
                    onChange={(e) => updateSetting('includeQRCode', e.target.checked)}
                  />
                </FormControl>
              </SimpleGrid>

              <FormControl display="flex" alignItems="center">
                <FormLabel mb="0">Přidat podpis</FormLabel>
                <Switch
                  isChecked={settings.includeSignature}
                  onChange={(e) => updateSetting('includeSignature', e.target.checked)}
                />
              </FormControl>
            </VStack>
          </CardBody>
        </Card>

        {/* Email Templates */}
        <Card>
          <CardHeader>
            <Heading size="md">Šablony emailů</Heading>
          </CardHeader>
          <CardBody>
            <VStack spacing={4} align="stretch">
              <FormControl>
                <FormLabel>Předmět emailu</FormLabel>
                <Input
                  value={settings.emailSubject}
                  onChange={(e) => updateSetting('emailSubject', e.target.value)}
                  placeholder="Faktura č. {invoiceNumber}"
                />
                <Text fontSize="sm" color="gray.600" mt={1}>
                  Dostupné proměnné: {'{invoiceNumber}'}, {'{customerName}'}, {'{dueDate}'}, {'{totalAmount}'}
                </Text>
              </FormControl>

              <FormControl>
                <FormLabel>Tělo emailu</FormLabel>
                <Textarea
                  value={settings.emailBody}
                  onChange={(e) => updateSetting('emailBody', e.target.value)}
                  placeholder="Dobrý den,..."
                  rows={6}
                />
                <Text fontSize="sm" color="gray.600" mt={1}>
                  Dostupné proměnné: {'{invoiceNumber}'}, {'{customerName}'}, {'{dueDate}'}, {'{totalAmount}'}, {'{supplierName}'}
                </Text>
              </FormControl>
            </VStack>
          </CardBody>
        </Card>

        {/* Payment and Legal Information */}
        <Card>
          <CardHeader>
            <Heading size="md">Platební a právní informace</Heading>
          </CardHeader>
          <CardBody>
            <VStack spacing={4} align="stretch">
              <FormControl>
                <FormLabel>Instrukce pro platbu</FormLabel>
                <Textarea
                  value={settings.paymentInstructions}
                  onChange={(e) => updateSetting('paymentInstructions', e.target.value)}
                  placeholder="Platba provedená bankovním převodem..."
                  rows={3}
                />
                <Text fontSize="sm" color="gray.600" mt={1}>
                  Dostupné proměnné: {'{bankAccount}'}, {'{iban}'}, {'{swift}'}, {'{variableSymbol}'}
                </Text>
              </FormControl>

              <FormControl>
                <FormLabel>Právní poznámka</FormLabel>
                <Textarea
                  value={settings.legalNote}
                  onChange={(e) => updateSetting('legalNote', e.target.value)}
                  placeholder="Podle § 13 Zákona o dani z přidané hodnoty..."
                  rows={3}
                />
              </FormControl>
            </VStack>
          </CardBody>
        </Card>

        {/* Preview */}
        <Card>
          <CardHeader>
            <Heading size="md">Náhled proměnných</Heading>
          </CardHeader>
          <CardBody>
            <Alert status="info">
              <AlertIcon />
              <VStack align="start" spacing={1}>
                <Text fontWeight="bold">Dostupné proměnné pro šablony:</Text>
                <Text>• {'{invoiceNumber}'} - Číslo faktury</Text>
                <Text>• {'{customerName}'} - Jméno zákazníka</Text>
                <Text>• {'{dueDate}'} - Datum splatnosti</Text>
                <Text>• {'{totalAmount}'} - Celková částka</Text>
                <Text>• {'{supplierName}'} - Název vaší firmy</Text>
                <Text>• {'{bankAccount}'} - Číslo účtu</Text>
                <Text>• {'{iban}'} - IBAN</Text>
                <Text>• {'{swift}'} - SWIFT kód</Text>
                <Text>• {'{variableSymbol}'} - Variabilní symbol</Text>
              </VStack>
            </Alert>
          </CardBody>
        </Card>
        </VStack>
      </Container>
    </AdminLayout>
  );
};

export default InvoiceSettingsPage;
